/**
 * Maps between the controller-scoped resource.json document and the LC-191 local-config form.
 *
 * resource.json is controller-scoped (`resources: []`, potentially several batteries). The form edits
 * exactly the entry whose `id === resourceUuid`, and ONLY the mapped leaf paths under its `steering`
 * block. Everything else — sibling resources, `binding`, unmapped trickle keys, anything not
 * surfaced — is preserved verbatim (the device silently defaults any dropped key, so a dropped key
 * is lost config). Unit tokens are preserved on unchanged fields.
 */
import {
  formatKwPerSecondToPowerRampRate,
  formatKwToActivePower,
  formatPercentToFraction,
  formatSecondsToPeriod,
  parseActivePowerToKw,
  parseFractionToPercent,
  parsePeriodToSeconds,
  parsePowerRampRateToKwPerSecond,
} from '@/features/resource/configuration/utils/resourceJsonUnits'

type JsonObject = Record<string, unknown>

/** Allowed schema enum values for the two FFR knobs. */
export const FFR_FREQUENCY_THRESHOLDS = ['49.5 Hz', '49.6 Hz', '49.7 Hz'] as const
export const FFR_MINIMUM_ACTIVATION_DURATIONS = ['5 s', '30 s'] as const
export type FfrFrequencyThreshold = (typeof FFR_FREQUENCY_THRESHOLDS)[number]
export type FfrMinimumActivationDuration = (typeof FFR_MINIMUM_ACTIVATION_DURATIONS)[number]

export type ResourceLocalConfigFormValues = {
  // Battery picker (create-entry mode only — null and untouched in other modes).
  batteryRef: string | null
  // Operational limits
  maxSocPercent: number | null
  minSocPercent: number | null
  // General battery settings
  maxChargePowerKw: number | null
  maxDischargePowerKw: number | null
  chargeSpeedKwPerSecond: number | null
  dischargeSpeedKwPerSecond: number | null
  restSetpointKw: number | null
  // Trickle / SoC management
  targetSocEnabled: boolean
  targetSocPercent: number | null
  targetSocSecondsHysteresis: number | null
  targetSocSecondsReset: number | null
  trickleChargePowerKw: number | null
  trickleChargeSpeedKwPerSecond: number | null
  trickleDischargePowerKw: number | null
  trickleDischargeSpeedKwPerSecond: number | null
  trickleRestSetpointKw: number | null
  // Market programs — FCR
  fcrnMaxKw: number | null
  fcrnLowKw: number | null
  fcrnLerKw: number | null
  fcrDUpMaxKw: number | null
  fcrDUpLowKw: number | null
  fcrDDownMaxKw: number | null
  fcrDDownLowKw: number | null
  // Market programs — FFR
  ffrFrequencyThreshold: FfrFrequencyThreshold | null
  ffrMinimumActivationDuration: FfrMinimumActivationDuration | null
  ffrMaxKw: number | null
  ffrLowKw: number | null
  // Market programs — mFRR
  mfrrRampTimeSeconds: number | null
  mfrrUpMaxKw: number | null
  mfrrUpLowKw: number | null
  mfrrDownMaxKw: number | null
  mfrrDownLowKw: number | null
  // Market programs — aFRR
  afrrMaxKw: number | null
  // Per-program participation toggles. Authoritative for whether a program's block exists on save:
  // OFF deletes any existing `marketPrograms.<program>` block, ON inserts `{}` (or populated leaves).
  // Wholesale has no leaves so its presence is purely toggle-driven.
  fcrParticipating: boolean
  ffrParticipating: boolean
  mfrrParticipating: boolean
  afrrParticipating: boolean
  wholesaleParticipating: boolean
}

type QuantityKind = 'fraction' | 'power' | 'ramp' | 'period'

/** Keys of `T` whose value type is `number | null`. Excludes string/boolean/enum-typed fields. */
type NumericFieldOf<T> = {
  [K in keyof T]-?: T[K] extends number | null ? K : never
}[keyof T]

/** Keys of `T` whose value type is exactly `boolean` (the participation toggles). */
type BooleanFieldOf<T> = {
  [K in keyof T]-?: T[K] extends boolean ? K : never
}[keyof T]

type NumericField = NumericFieldOf<ResourceLocalConfigFormValues>
type BooleanField = BooleanFieldOf<ResourceLocalConfigFormValues>

/** Typed numeric-leaf view of the form-values bag. Lets `values[field]` index without unsafe casts. */
type NumericFieldsView = Record<NumericField, number | null>

type NumericLeafSpec = {
  field: NumericField
  /** Path relative to the resource entry's `steering` object. */
  path: string[]
  kind: QuantityKind
}

const LEAF_SPECS: NumericLeafSpec[] = [
  // Operational limits
  { field: 'maxSocPercent', path: ['socThresholds', 'maxSoc'], kind: 'fraction' },
  { field: 'minSocPercent', path: ['socThresholds', 'minSoc'], kind: 'fraction' },
  // NOTE: `socThresholds.warnMaxSoc` / `socThresholds.warnMinSoc` are intentionally NOT mapped —
  // the misnamed "Alerts" section was retired (the controller treats these as an internal
  // priority-band signal, not user-facing alerts). The subtree round-trips verbatim via the
  // structuredClone pattern: untouched leaves under the same parent keep their original tokens.
  // General battery settings
  { field: 'maxChargePowerKw', path: ['maxPowerLimits', 'charge'], kind: 'power' },
  { field: 'maxDischargePowerKw', path: ['maxPowerLimits', 'discharge'], kind: 'power' },
  { field: 'chargeSpeedKwPerSecond', path: ['maxRampSpeed', 'charge'], kind: 'ramp' },
  { field: 'dischargeSpeedKwPerSecond', path: ['maxRampSpeed', 'discharge'], kind: 'ramp' },
  { field: 'restSetpointKw', path: ['restSetpoint'], kind: 'power' },
  // Trickle
  { field: 'targetSocPercent', path: ['trickle', 'targetSoc'], kind: 'fraction' },
  { field: 'targetSocSecondsHysteresis', path: ['trickle', 'targetSocSecondsHysteresis'], kind: 'period' },
  { field: 'targetSocSecondsReset', path: ['trickle', 'targetSocSecondsReset'], kind: 'period' },
  { field: 'trickleChargePowerKw', path: ['trickle', 'setpoint', 'charge'], kind: 'power' },
  { field: 'trickleDischargePowerKw', path: ['trickle', 'setpoint', 'discharge'], kind: 'power' },
  { field: 'trickleChargeSpeedKwPerSecond', path: ['trickle', 'rampSpeed', 'charge'], kind: 'ramp' },
  { field: 'trickleDischargeSpeedKwPerSecond', path: ['trickle', 'rampSpeed', 'discharge'], kind: 'ramp' },
  { field: 'trickleRestSetpointKw', path: ['trickle', 'restSetpoint'], kind: 'power' },
  // NOTE: `steering.transformerGuard.*` is intentionally NOT mapped — the section was removed from
  // the UI as a legacy device feature, but the apply path preserves the subtree verbatim via
  // structuredClone. The form must never resurrect it.
  // Market programs — FCR PQ
  { field: 'fcrnMaxKw', path: ['marketPrograms', 'fcr', 'pq', 'fcrnMax'], kind: 'power' },
  { field: 'fcrnLowKw', path: ['marketPrograms', 'fcr', 'pq', 'fcrnLow'], kind: 'power' },
  { field: 'fcrnLerKw', path: ['marketPrograms', 'fcr', 'pq', 'fcrnLer'], kind: 'power' },
  { field: 'fcrDUpMaxKw', path: ['marketPrograms', 'fcr', 'pq', 'fcrDUpMax'], kind: 'power' },
  { field: 'fcrDUpLowKw', path: ['marketPrograms', 'fcr', 'pq', 'fcrDUpLow'], kind: 'power' },
  { field: 'fcrDDownMaxKw', path: ['marketPrograms', 'fcr', 'pq', 'fcrDDownMax'], kind: 'power' },
  { field: 'fcrDDownLowKw', path: ['marketPrograms', 'fcr', 'pq', 'fcrDDownLow'], kind: 'power' },
  // Market programs — FFR PQ (enums handled separately)
  { field: 'ffrMaxKw', path: ['marketPrograms', 'ffr', 'pq', 'ffrMax'], kind: 'power' },
  { field: 'ffrLowKw', path: ['marketPrograms', 'ffr', 'pq', 'ffrLow'], kind: 'power' },
  // Market programs — mFRR
  { field: 'mfrrRampTimeSeconds', path: ['marketPrograms', 'mfrr', 'rampTime'], kind: 'period' },
  { field: 'mfrrUpMaxKw', path: ['marketPrograms', 'mfrr', 'pq', 'mfrrUpMax'], kind: 'power' },
  { field: 'mfrrUpLowKw', path: ['marketPrograms', 'mfrr', 'pq', 'mfrrUpLow'], kind: 'power' },
  { field: 'mfrrDownMaxKw', path: ['marketPrograms', 'mfrr', 'pq', 'mfrrDownMax'], kind: 'power' },
  { field: 'mfrrDownLowKw', path: ['marketPrograms', 'mfrr', 'pq', 'mfrrDownLow'], kind: 'power' },
  // Market programs — aFRR
  { field: 'afrrMaxKw', path: ['marketPrograms', 'afrr', 'pq', 'afrrMax'], kind: 'power' },
]

const TARGET_SOC_ENABLED_PATH = ['trickle', 'targetSocEnabled']
const FFR_FREQUENCY_THRESHOLD_PATH = ['marketPrograms', 'ffr', 'frequencyThreshold']
const FFR_MIN_ACTIVATION_PATH = ['marketPrograms', 'ffr', 'minimumActivationDuration']

function isObject(value: unknown): value is JsonObject {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function getByPath(root: JsonObject, path: string[]): unknown {
  let current: unknown = root
  for (const key of path) {
    if (!isObject(current)) return undefined
    current = current[key]
  }
  return current
}

function parseQuantity(kind: QuantityKind, raw: string): number {
  switch (kind) {
    case 'fraction':
      return parseFractionToPercent(raw)
    case 'power':
      return parseActivePowerToKw(raw)
    case 'ramp':
      return parsePowerRampRateToKwPerSecond(raw)
    case 'period':
      return parsePeriodToSeconds(raw)
  }
}

function formatQuantity(kind: QuantityKind, value: number): string {
  switch (kind) {
    case 'fraction':
      return formatPercentToFraction(value)
    case 'power':
      return formatKwToActivePower(value)
    case 'ramp':
      return formatKwPerSecondToPowerRampRate(value)
    case 'period':
      return formatSecondsToPeriod(value)
  }
}

function isFfrFrequencyThreshold(value: unknown): value is FfrFrequencyThreshold {
  return typeof value === 'string' && (FFR_FREQUENCY_THRESHOLDS as readonly string[]).includes(value)
}

function isFfrMinimumActivationDuration(value: unknown): value is FfrMinimumActivationDuration {
  return typeof value === 'string' && (FFR_MINIMUM_ACTIVATION_DURATIONS as readonly string[]).includes(value)
}

export function parseResourceJsonDocument(value: string): JsonObject {
  const parsed: unknown = JSON.parse(value)
  if (!isObject(parsed)) {
    throw new Error('resource.json root is not an object')
  }
  return parsed
}

/**
 * Form values to pre-fill when the user is creating local config from scratch (no steering block
 * exists yet). Mirrors the device-side schema defaults documented in LC-168, except for the
 * battery-nameplate-specific power limits which the user must fill explicitly.
 */
export function getLocalConfigCreateDefaults(): ResourceLocalConfigFormValues {
  return {
    batteryRef: null,
    // Operational limits (LC-168 defaults; user can override)
    maxSocPercent: 95,
    minSocPercent: 5,
    // General battery settings — power limits are nameplate-specific and REQUIRED (no default).
    maxChargePowerKw: null,
    maxDischargePowerKw: null,
    chargeSpeedKwPerSecond: null,
    dischargeSpeedKwPerSecond: null,
    restSetpointKw: 0,
    // Trickle / SoC management
    targetSocEnabled: false,
    targetSocPercent: null,
    targetSocSecondsHysteresis: null,
    targetSocSecondsReset: null,
    trickleChargePowerKw: null,
    trickleChargeSpeedKwPerSecond: null,
    trickleDischargePowerKw: null,
    trickleDischargeSpeedKwPerSecond: null,
    trickleRestSetpointKw: null,
    // Market programs — all empty by default
    fcrnMaxKw: null,
    fcrnLowKw: null,
    fcrnLerKw: null,
    fcrDUpMaxKw: null,
    fcrDUpLowKw: null,
    fcrDDownMaxKw: null,
    fcrDDownLowKw: null,
    ffrFrequencyThreshold: null,
    ffrMinimumActivationDuration: null,
    ffrMaxKw: null,
    ffrLowKw: null,
    mfrrRampTimeSeconds: null,
    mfrrUpMaxKw: null,
    mfrrUpLowKw: null,
    mfrrDownMaxKw: null,
    mfrrDownLowKw: null,
    afrrMaxKw: null,
    fcrParticipating: false,
    ffrParticipating: false,
    mfrrParticipating: false,
    afrrParticipating: false,
    wholesaleParticipating: false,
  }
}

/** Returns the existing `binding.batteryRef` for the resource's entry, or null. */
export function getExistingBatteryRef(doc: JsonObject, resourceUuid: string): string | null {
  const entry = findResourceEntry(doc, resourceUuid)
  if (!entry) return null
  const binding = entry.binding
  if (!isObject(binding)) return null
  return typeof binding.batteryRef === 'string' ? binding.batteryRef : null
}

/** Locate the battery resource entry whose `id` matches the resource UUID, or null. */
export function findResourceEntry(doc: JsonObject, resourceUuid: string): JsonObject | null {
  const resources = doc.resources
  if (!Array.isArray(resources)) return null
  const entry = resources.find((r) => isObject(r) && r.id === resourceUuid)
  return isObject(entry) ? entry : null
}

/**
 * Read the form values from a resource.json document. Returns null when the resource has no
 * steering block (signals create-mode to the caller).
 *
 * May throw via `parseQuantity` if a string leaf at a known path is not a parseable quantity
 * (e.g. `socThresholds.maxSoc: "ninety-five"`). Callers should treat any throw as a corrupt-
 * document case — ResourceJsonEditor routes those into the corrupt-document panel.
 */
export function extractLocalConfig(doc: JsonObject, resourceUuid: string): ResourceLocalConfigFormValues | null {
  const entry = findResourceEntry(doc, resourceUuid)
  if (!entry || !isObject(entry.steering)) {
    return null
  }
  const steering = entry.steering

  const values = {} as ResourceLocalConfigFormValues
  const numericFields: NumericFieldsView = values
  for (const spec of LEAF_SPECS) {
    const raw = getByPath(steering, spec.path)
    numericFields[spec.field] = typeof raw === 'string' ? parseQuantity(spec.kind, raw) : null
  }
  values.targetSocEnabled = getByPath(steering, TARGET_SOC_ENABLED_PATH) === true
  const ffrFt = getByPath(steering, FFR_FREQUENCY_THRESHOLD_PATH)
  values.ffrFrequencyThreshold = isFfrFrequencyThreshold(ffrFt) ? ffrFt : null
  const ffrMa = getByPath(steering, FFR_MIN_ACTIVATION_PATH)
  values.ffrMinimumActivationDuration = isFfrMinimumActivationDuration(ffrMa) ? ffrMa : null
  values.batteryRef =
    isObject(entry.binding) && typeof entry.binding.batteryRef === 'string' ? entry.binding.batteryRef : null

  const market = isObject(steering.marketPrograms) ? steering.marketPrograms : undefined
  values.fcrParticipating = Boolean(market && isObject(market.fcr))
  values.ffrParticipating = Boolean(market && isObject(market.ffr))
  values.mfrrParticipating = Boolean(market && isObject(market.mfrr))
  values.afrrParticipating = Boolean(market && isObject(market.afrr))
  values.wholesaleParticipating = Boolean(market && isObject(market.wholesale))

  return values
}

export type ApplyOptions = {
  /**
   * Used only when there is no existing `resources[]` entry for `resourceUuid` (create-entry mode).
   * The new entry is created with `binding.batteryRef = creationBatteryRef`. Ignored when an entry
   * for the resource already exists.
   */
  creationBatteryRef?: string | null
}

function ensurePath(root: JsonObject, path: string[]): JsonObject {
  let cur: JsonObject = root
  const traversed: string[] = []
  for (const key of path) {
    traversed.push(key)
    const child = cur[key]
    if (isObject(child)) {
      cur = child
    } else if (child === undefined) {
      // Create missing intermediate object (sub-block creation path).
      const fresh: JsonObject = {}
      cur[key] = fresh
      cur = fresh
    } else {
      // Defensive throw — refuse to overwrite a non-object intermediate. The BFF should never return
      // such data, but this stops silent destruction of unexpected payloads.
      throw new Error(`Malformed resource.json: ${traversed.join('.')} is not an object (got ${typeof child})`)
    }
  }
  return cur
}

/** Walk to an existing parent object without creating intermediates. Returns null if any step
 * along the way is missing or non-object — handy for delete-on-null leaf paths where we don't
 * want to materialise empty sub-blocks just to remove a leaf that wasn't there to begin with. */
function findExistingParent(root: JsonObject, path: string[]): JsonObject | null {
  let cur: JsonObject = root
  for (const key of path) {
    const child = cur[key]
    if (!isObject(child)) return null
    cur = child
  }
  return cur
}

/**
 * After a leaf delete, walk back up the ancestor chain and drop any object that became `{}` as
 * a direct consequence — paired-clears of `maxPowerLimits.{charge,discharge}` would otherwise
 * leave `maxPowerLimits: {}` on disk, which is visual noise and a no-op for the device.
 *
 * Stops at the first non-empty ancestor and never touches `root` itself — the loop bound
 * `i >= 1` means the shortest `ancestorPath` we ever evaluate has length 1, i.e. a direct child
 * of `steering`. Only `Record<string, unknown>` parents are pruned; the function never reaches
 * an array element or the root document.
 */
function pruneEmptyAncestors(root: JsonObject, leafPath: string[]): void {
  for (let i = leafPath.length - 1; i >= 1; i--) {
    const ancestorPath = leafPath.slice(0, i)
    const ancestor = findExistingParent(root, ancestorPath)
    if (!ancestor) break
    if (Object.keys(ancestor).length !== 0) break
    const grandParentPath = ancestorPath.slice(0, -1)
    const grandParent = findExistingParent(root, grandParentPath)
    if (!grandParent) break
    const ancestorKey = ancestorPath.at(-1)!
    delete grandParent[ancestorKey]
  }
}

/**
 * Only the `trickle.targetSoc*` leaves are gated on the section's enable toggle (`targetSocEnabled`):
 * when the section is OFF they're cleared on save. The `setpoint` / `rampSpeed` / `restSetpoint`
 * leaves are deliberately NOT wiped — they round-trip via delete-on-null. The controller firmware
 * (TrickleCharger / EngineWrapper) keeps consuming `trickle.rampSpeed` as the ramp-speed fallback for
 * operator soc-set targets (LC-293) even when `targetSocEnabled` is false, so wiping the section wholesale
 * would silently change device behaviour. The UI disables (but preserves) these inputs while OFF.
 */
function isTargetSocLeaf(spec: NumericLeafSpec): boolean {
  return (
    spec.field === 'targetSocPercent' ||
    spec.field === 'targetSocSecondsHysteresis' ||
    spec.field === 'targetSocSecondsReset'
  )
}

function hasAnyValueToWrite(values: ResourceLocalConfigFormValues): boolean {
  const numericFields: NumericFieldsView = values
  for (const spec of LEAF_SPECS) {
    if (typeof numericFields[spec.field] === 'number') return true
  }
  if (values.targetSocEnabled === true) return true
  if (values.ffrFrequencyThreshold !== null) return true
  if (values.ffrMinimumActivationDuration !== null) return true
  if (values.fcrParticipating) return true
  if (values.ffrParticipating) return true
  if (values.mfrrParticipating) return true
  if (values.afrrParticipating) return true
  if (values.wholesaleParticipating) return true
  return false
}

type MarketProgramKey = 'fcr' | 'ffr' | 'mfrr' | 'afrr' | 'wholesale'

const MARKET_PROGRAM_PARTICIPATING_FIELD: Record<MarketProgramKey, BooleanField> = {
  fcr: 'fcrParticipating',
  ffr: 'ffrParticipating',
  mfrr: 'mfrrParticipating',
  afrr: 'afrrParticipating',
  wholesale: 'wholesaleParticipating',
}

function isProgramParticipating(values: ResourceLocalConfigFormValues, program: MarketProgramKey): boolean {
  return values[MARKET_PROGRAM_PARTICIPATING_FIELD[program]] === true
}

/**
 * Write or clear one FFR enum leaf under `marketPrograms.ffr`. Mirrors the numeric delete-on-null
 * rule: a non-null value is written (on change); a null value deletes a previously-set leaf and
 * prunes the now-empty parent. Caller must have already confirmed FFR participates.
 */
function applyFfrEnumLeaf(
  steering: JsonObject,
  key: 'frequencyThreshold' | 'minimumActivationDuration',
  value: string | null,
): void {
  if (value === null) {
    const ffr = findExistingParent(steering, ['marketPrograms', 'ffr'])
    if (ffr && Object.hasOwn(ffr, key)) {
      delete ffr[key]
      pruneEmptyAncestors(steering, ['marketPrograms', 'ffr', key])
    }
    return
  }
  const ffr = ensurePath(steering, ['marketPrograms', 'ffr'])
  if (ffr[key] !== value) {
    ffr[key] = value
  }
}

/**
 * Produce a new resource.json document with the form values applied to (or used to create) the
 * matching `resources[]` entry's steering block.
 *
 * - If the entry is missing: a new entry is appended using `options.creationBatteryRef` for
 *   `binding.batteryRef`. Sibling `resources[]` entries are preserved verbatim.
 * - If steering is missing: created when at least one value is provided; otherwise the document
 *   is returned untouched.
 * - For each leaf with a non-null form value: the parent sub-block chain is created on demand
 *   (marketPrograms.<program>, trickle.setpoint, etc.). Unchanged leaves keep their original unit
 *   token; only changed (or newly-created) leaves are written.
 * - `steering.transformerGuard.*` and `steering.socThresholds.warnMaxSoc/warnMinSoc` are
 *   intentionally NOT surfaced (legacy / misnamed sections that were retired). Because
 *   structuredClone preserves the rest of the document and no LEAF_SPEC touches those keys, any
 *   existing data under them round-trips verbatim through save.
 * - targetSocEnabled / FFR enums follow the same skip-if-unchanged + skip-if-absent-and-default
 *   rule so no-op saves never introduce redundant keys.
 * - Market programs are governed by per-program `*Participating` booleans: ON ensures
 *   `marketPrograms.<program>` exists ({} if no leaves filled); OFF deletes the block. Wholesale
 *   is symmetric with the rest — no more never-delete special case.
 */
export function applyLocalConfig(
  doc: JsonObject,
  resourceUuid: string,
  values: ResourceLocalConfigFormValues,
  options: ApplyOptions = {},
): JsonObject {
  const next = structuredClone(doc)

  // Ensure resources[] exists (preserving any pre-existing siblings).
  if (!Array.isArray(next.resources)) {
    next.resources = []
  }
  const resources = next.resources as unknown[]

  // Find the entry; create it if missing (requires a batteryRef).
  let entry: JsonObject | undefined
  for (const candidate of resources) {
    if (isObject(candidate) && candidate.id === resourceUuid) {
      entry = candidate
      break
    }
  }
  if (!entry) {
    if (!options.creationBatteryRef) {
      throw new Error('Cannot create resource entry without a batteryRef')
    }
    entry = {
      type: 'battery',
      id: resourceUuid,
      binding: { batteryRef: options.creationBatteryRef },
    }
    resources.push(entry)
  }

  // Steering: created on demand iff there is anything to write.
  if (!isObject(entry.steering)) {
    if (!hasAnyValueToWrite(values)) return next
    entry.steering = {}
  }
  const steering = entry.steering as JsonObject

  // Numeric leaves — three branches:
  //   1. Market-program leaves are gated by the per-program participation toggle. When the
  //      toggle is OFF the participation-reconciliation loop below deletes the whole block, so
  //      writes are skipped here (would resurrect / waste work). When the program IS participating,
  //      individual leaves follow the same delete-on-null rule as non-market leaves — clearing one
  //      PQ field (while keeping the program active) deletes that leaf instead of round-tripping the
  //      original value back to disk.
  //   2. The `trickle.targetSoc*` leaves are gated on the trickle section toggle `targetSocEnabled`:
  //      when it's OFF they're cleared on save. The trickle `setpoint`/`rampSpeed`/`restSetpoint` leaves
  //      fall through to branch 3 (delete-on-null round-trip) — the firmware still consumes
  //      `trickle.rampSpeed` as the soc-set ramp fallback (LC-293) when targetSocEnabled is false, so they
  //      must NOT be wiped just because the section is off. The UI disables these inputs (values stay
  //      visible) while the section is off.
  //   3. All other non-market leaves (operational limits, max-ramp, rest-setpoint) use delete-on-
  //      null: was-present → now-null transitions the user typed to clear a previously-set leaf, and
  //      silently skipping the write would leave the original value on disk (visible as the cleared
  //      field reappearing on reload).
  const numericFields: NumericFieldsView = values
  for (const spec of LEAF_SPECS) {
    const newValue = numericFields[spec.field]
    const isMarketLeaf = spec.path[0] === 'marketPrograms'

    if (isMarketLeaf) {
      const program = spec.path[1] as MarketProgramKey
      // Program OFF: skip — the reconciliation loop below deletes the whole block.
      if (!isProgramParticipating(values, program)) continue
      // Participating program: delete a cleared leaf (was-present → now-null), mirroring the
      // non-market branch, so clearing one PQ field doesn't silently round-trip the old value.
      if (newValue === null) {
        const parent = findExistingParent(steering, spec.path.slice(0, -1))
        const leafKey = spec.path.at(-1)!
        if (parent && Object.hasOwn(parent, leafKey)) {
          delete parent[leafKey]
          pruneEmptyAncestors(steering, spec.path)
        }
        continue
      }
      if (typeof newValue !== 'number') continue
      const parent = ensurePath(steering, spec.path.slice(0, -1))
      const leafKey = spec.path.at(-1)!
      const original = parent[leafKey]
      if (typeof original === 'string' && parseQuantity(spec.kind, original) === newValue) {
        continue
      }
      parent[leafKey] = formatQuantity(spec.kind, newValue)
      continue
    }

    // Non-market leaf. Determine whether this iteration is a "delete" or a "write".
    const shouldDelete = newValue === null || (isTargetSocLeaf(spec) && !values.targetSocEnabled)
    if (shouldDelete) {
      const parent = findExistingParent(steering, spec.path.slice(0, -1))
      const leafKey = spec.path.at(-1)!
      if (parent && Object.hasOwn(parent, leafKey)) {
        delete parent[leafKey]
        // Prune any ancestor block that became `{}` as a direct consequence — keeps the on-disk
        // shape tidy after paired-clears (maxPowerLimits) and targetSoc toggle-offs (trickle).
        pruneEmptyAncestors(steering, spec.path)
      }
      continue
    }

    if (typeof newValue !== 'number') continue
    const parent = ensurePath(steering, spec.path.slice(0, -1))
    const leafKey = spec.path.at(-1)!
    const original = parent[leafKey]
    if (typeof original === 'string' && parseQuantity(spec.kind, original) === newValue) {
      continue // unchanged — preserve the original unit token
    }
    parent[leafKey] = formatQuantity(spec.kind, newValue)
  }

  // Boolean: targetSocEnabled — when the key was present, write only on change; when the key was
  // absent, only introduce it if the user opted in (schema default is false, so writing false would
  // silently add a redundant key).
  if (values.targetSocEnabled === true) {
    if (!isObject(steering.trickle)) {
      steering.trickle = {}
    }
    const trickle = steering.trickle as JsonObject
    if (trickle.targetSocEnabled !== true) {
      trickle.targetSocEnabled = true
    }
  } else if (isObject(steering.trickle) && steering.trickle.targetSocEnabled === true) {
    // User toggled off an originally-true value — write the explicit false.
    steering.trickle.targetSocEnabled = false
  }

  // FFR enums — only while FFR participates: write the picked value, or delete a cleared one (same
  // was-present → now-null delete-on-null rule as the numeric leaves, so clearing an enum doesn't
  // round-trip the old value back to disk).
  if (values.ffrParticipating) {
    applyFfrEnumLeaf(steering, 'frequencyThreshold', values.ffrFrequencyThreshold)
    applyFfrEnumLeaf(steering, 'minimumActivationDuration', values.ffrMinimumActivationDuration)
  }

  // Per-program participation reconciliation (LC-191 deletable-toggle redesign — the toggle is the
  // single source of truth for whether `marketPrograms.<program>` exists on disk):
  //   participating === false → delete the block if present (every program, including wholesale).
  //   participating === true  → ensure the block exists (insert `{}` when absent and no leaves).
  // Leaf writes above already populated participating blocks; this loop only handles ensuring
  // an empty marker exists when the user opted in without filling anything.
  const programs: readonly MarketProgramKey[] = ['fcr', 'ffr', 'mfrr', 'afrr', 'wholesale']
  const market = isObject(steering.marketPrograms) ? (steering.marketPrograms as JsonObject) : null
  for (const program of programs) {
    const participating = isProgramParticipating(values, program)
    if (!participating) {
      if (market && Object.hasOwn(market, program)) {
        delete market[program]
      }
    } else {
      // ensurePath materializes `marketPrograms.<program>` as `{}` when the path was newly created;
      // populated leaves were already written above. No further work needed on the returned object.
      ensurePath(steering, ['marketPrograms', program])
    }
  }

  return next
}
